import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router";

import { useMe } from "@/auth/useMe";
import { Pagination } from "@/components/Pagination";
import {
  Card,
  EmptyState,
  ErrorState,
  Input,
  Page,
  Seg,
  Select,
  Spinner,
} from "@/components/ui";
import { LeadCard } from "@/leads/LeadCard";
import type { QueueFilter } from "@/leads/queries";
import {
  CHANNELS,
  channelFromParam,
  queueFilterFromParam,
  useAgents,
  useLeads,
} from "@/leads/queries";

const QUEUES: readonly QueueFilter[] = ["pending", "done", "all"];

/**
 * Recepción — the lead intake queue (D13).
 *
 * The only multi-table intake path in the system, so it is deliberately one
 * screen and one action: pick who takes it, press the button, and the server
 * does the whole `Company` → `Branch` → `Contact` → `ContactPhone` → `Activity`
 * chain inside a single transaction. Splitting that across a wizard would put
 * a multi-table invariant in the browser, where a half-finished one survives a
 * closed tab (.docs/01 §4.6).
 */
export function Reception() {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const [params, setParams] = useSearchParams();

  const queue = queueFilterFromParam(params.get("cola"));
  const channel = channelFromParam(params.get("canal"));
  const search = params.get("q") ?? "";
  const page = Math.max(1, Number(params.get("page") ?? 1) || 1);

  // Local while typing, in the URL once they pause — same as the company list.
  const [draft, setDraft] = useState(search);
  useEffect(() => setDraft(search), [search]);
  useEffect(() => {
    if (draft === search) return;
    const timer = setTimeout(() => {
      setParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          if (draft) next.set("q", draft);
          else next.delete("q");
          next.delete("page");
          return next;
        },
        { replace: true },
      );
    }, 300);
    return () => clearTimeout(timer);
  }, [draft, search, setParams]);

  // `can_triage` is the same check `IsTriageRole` makes server-side. Asking for
  // the agent list without it earns a 403, and a dropdown that 403s on load is
  // worse than one that is never rendered.
  const canTriage = me?.can_triage === true || me?.is_manager === true;
  const agents = useAgents(canTriage);
  const { data, isPending, isError, isFetching, error, refetch } = useLeads({
    queue,
    channel,
    search,
    page,
  });

  function update(key: string, value: string) {
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      if (value) next.set(key, value);
      else next.delete(key);
      if (key !== "page") next.delete("page");
      return next;
    });
  }

  return (
    <Page
      title={t("leads.title")}
      subtitle={data ? t("leads.waiting", { count: data.count }) : t("leads.subtitle")}
      actions={
        // The capture window for an inbound call. Behind the same check as the
        // queue's own buttons: without it the screen it opens answers 403.
        canTriage ? (
          <Link to="/recepcion/nueva" className="btn btn-primary">
            {t("leads.newCall")}
          </Link>
        ) : null
      }
    >
      <div className="mb-4 flex flex-wrap gap-3">
        {/* The system's own segmented control, so the three queues read the
            same as every other one-of-a-small-set choice in the app. */}
        <Seg<QueueFilter>
          name="reception-queue"
          value={queue}
          onChange={(value) => update("cola", value === "pending" ? "" : value)}
          options={QUEUES.map((option) => ({
            value: option,
            label: t(`leads.queue.${option}`),
          }))}
        />

        <Input
          type="search"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder={t("leads.searchPlaceholder")}
          aria-label={t("leads.search")}
          className="min-w-64 flex-1"
        />

        <Select
          value={channel}
          onChange={(event) => update("canal", event.target.value)}
          aria-label={t("leads.filterChannel")}
          className="w-auto"
        >
          <option value="">{t("leads.allChannels")}</option>
          {CHANNELS.map((option) => (
            <option key={option} value={option}>
              {t(`leads.channel.${option}`)}
            </option>
          ))}
        </Select>
      </div>

      {!canTriage ? (
        // Read-only, and said out loud. `IsTriageRole` lets any signed-in user
        // read the queue; only triage roles may act on it. A seller staring at
        // cards with no button should be told why rather than left guessing.
        <p className="mb-3 text-sm text-mute-600">{t("leads.readOnly")}</p>
      ) : null}

      {isPending ? (
        <Card>
          <Spinner label={t("leads.loading")} />
        </Card>
      ) : isError ? (
        <Card>
          <ErrorState
            message={error instanceof Error ? error.message : t("leads.loadFailed")}
            onRetry={() => void refetch()}
          />
        </Card>
      ) : data.results.length === 0 ? (
        <Card>
          <EmptyState>
            {queue === "pending" ? t("leads.queueClear") : t("leads.noMatches")}
          </EmptyState>
        </Card>
      ) : (
        <>
          <ul className="space-y-3">
            {data.results.map((lead) => (
              <LeadCard
                key={lead.id}
                lead={lead}
                agents={agents.data ?? []}
                canTriage={canTriage}
              />
            ))}
          </ul>
          <Card>
            <Pagination
              page={page}
              count={data.count}
              shown={data.results.length}
              isFetching={isFetching}
              onChange={(next) => update("page", String(next))}
            />
          </Card>
        </>
      )}
    </Page>
  );
}
